import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { TextField, Typography } from '@mui/material';
import NodeRepeatableList from './NodeRepeatableList';
import NodeSelect from './NodeSelect';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeTextSearchRules — Repeatable text search rules.
 * Used by: Await External Event (text search resume condition).
 *
 * Each rule has: field to search, match type (contains/equals/regex), and value.
 */

const RuleFields = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const Row = styled('div')({
  display: 'flex',
  gap: 6,
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': { borderColor: theme.palette.text.primary, borderWidth: '1px' },
  },
  '& .MuiInputBase-input': {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
}));

const MATCH_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'regex', label: 'Regex' },
];

const NodeTextSearchRules = React.memo(function NodeTextSearchRules({
  label = 'Text Search Rules',
  rules = [],
  onRulesChange,
  matchOptions = MATCH_OPTIONS,
  addLabel = 'Add Rule',
  sx,
}) {
  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      <NodeRepeatableList
        label={label}
        items={rules}
        onItemsChange={onRulesChange}
        createItem={() => ({ field: '', matchType: 'contains', value: '' })}
        addLabel={addLabel}
        renderItem={(item, index, { updateItem }) => (
          <RuleFields>
            <StyledTextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="Field to search"
              value={item.field || ''}
              onChange={(e) => updateItem(index, { ...item, field: e.target.value })}
            />
            <Row>
              <NodeSelect
                value={item.matchType || 'contains'}
                onChange={(val) => updateItem(index, { ...item, matchType: val })}
                options={matchOptions}
                sx={{ minWidth: 120 }}
              />
              <StyledTextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Value"
                value={item.value || ''}
                onChange={(e) => updateItem(index, { ...item, value: e.target.value })}
              />
            </Row>
          </RuleFields>
        )}
        sx={sx}
      />
    </div>
  );
});

export default NodeTextSearchRules;
